import * as React from "react"

import { cn } from "../../cn"
import { CopyButton } from "../CopyButton/CopyButton"

export interface CodeSnippetProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "children" | "onCopy"
> {
  /** The text shown, and copied in full by the copy button. */
  children: string
  /** Accessible name and tooltip of the copy button. Defaults to "Copy". */
  copyLabel?: string
  /** Called with the copied text after a successful copy. */
  onCopy?: (value: string) => void
}

/**
 * Bordered block of preformatted text with a copy button beside it. The text
 * keeps its line breaks and indentation and wraps long lines, so multi-line
 * values such as formatted JSON read in place without horizontal scrolling.
 */
const CodeSnippet = React.forwardRef<HTMLDivElement, CodeSnippetProps>(
  ({ children, copyLabel = "Copy", onCopy, className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "flex min-w-0 items-start gap-2 rounded-md border border-coval-border bg-surface-default py-1 pl-3 pr-1",
        className,
      )}
      {...props}
    >
      <pre className="min-w-0 flex-1 py-1 font-mono text-sm whitespace-pre-wrap break-words text-coval-text-muted">
        {children}
      </pre>
      <CopyButton
        value={children}
        label={copyLabel}
        variant="ghost"
        onCopy={onCopy}
        className="shrink-0"
      />
    </div>
  ),
)

CodeSnippet.displayName = "CodeSnippet"

export { CodeSnippet }
